import * as React from 'react';

import type { DataGridSelection } from './DataGrid';

/**
 * Hook to handle DataGrid selection logic
 * @param data - Array of data items
 * @param setSelectedRows - Function to update selected rows state
 * @returns handleSelectionChange function compatible with DataGrid onSelectionChange
 */
export function useDataGridSelection<T extends { id: string | number }>(
  data: T[],
  setSelectedRows: (rows: T[]) => void,
) {
  const handleSelectionChange = React.useCallback(
    (keys: DataGridSelection) => {
      if (keys === 'all') {
        setSelectedRows(data);
      } else {
        const selectedItems = data.filter((item) => keys.has(String(item.id)));
        setSelectedRows(selectedItems);
      }
    },
    [data, setSelectedRows],
  );

  return handleSelectionChange;
}

export function useMemoDataGridRows<T extends { id: string | number }>(
  data: T[],
  selection?: DataGridSelection,
) {
  return React.useMemo(() => {
    if (!selection) return [];

    if (selection === 'all') {
      return data;
    } else {
      const selectedItems = data.filter((item) =>
        selection.has(String(item.id)),
      );
      return selectedItems;
    }
  }, [data, selection]);
}
